import AbstractQueryFactory from "api/lib/abstract_query/AbstractQueryFactory";
import AbstractQuery from "api/lib/abstract_query/interfaces/AbstractQuery";
import OrderBy from "api/lib/abstract_query/interfaces/OrderBy";
import ResultColumn from "api/lib/abstract_query/interfaces/ResultColumn";
import ColumnObject from "api/lib/data/interfaces/ColumnObject";
import * as _ from "lodash";
import * as React from "react";
import moment from "moment";
import sqlFormatter from "sql-formatter";
import {
  Button,
  Confirm,
  Container,
  Dimmer,
  Dropdown,
  DropdownProps,
  Grid,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Popup,
  Segment,
} from "semantic-ui-react";
import { cloneWithOutReference } from "../../utils/object";
import { Base, Event, IBaseProps, IBaseState } from "../Common/Base";
import MetadataObject from "api/lib/data/interfaces/MetadataObject";
import VersionRequestAction from "api/lib/data/interfaces/VersionRequestAction";
import VersionResult from "api/lib/data/interfaces/VersionResult";
import VersionSuccessResult from "api/lib/data/interfaces/VersionSuccessResult";
import VersionErrorResult from "api/lib/data/interfaces/VersionErrorResult";
import VersionTranslation from "api/lib/data/interfaces/VersionTranslation";
import VersionSuccessTranslation from "api/lib/data/interfaces/VersionSuccessTranslation";
import VersionErrorTranslation from "api/lib/data/interfaces/VersionErrorTranslation";
import VersionRequest from "api/lib/data/interfaces/VersionRequest";
import Query from "api/lib/data/Query";
import Version from "api/lib/data/Version";
import VersionObject from "api/lib/data/interfaces/VersionObject";
import FunctionName from "api/lib/abstract_query/interfaces/FunctionName";
import Func from "api/lib/abstract_query/interfaces/Func";
import ResultColumnType from "api/lib/abstract_query/interfaces/ResultColumnType";
import Column from "api/lib/abstract_query/interfaces/Column";
import MessageObject from "api/lib/data/interfaces/MessageObject";
import MessageType from "api/lib/data/interfaces/MessageType";
import VersionHistoryModal from "../CodeDiff/VersionHistoryModal";
import SearchBox from "./SearchBox";
import EditableLabel from "../Filter/widgets/core/EditableLabel";
import { toast } from "react-semantic-toasts";
import "react-semantic-toasts/styles/react-semantic-alert.css";
import Datasources from "api/lib/data/Datasources";
import BrowseColumns from "./BrowseColumns";
import { EditQueryNameModal } from "./EditQueryNameModal";
import { UsersStatus, UserStatus } from "../Session/withAuthorization";
import VisualizationType from "api/lib/visualizations/interfaces/VisualizationType";
import DataTable from "./DataTable";
const Filters = React.lazy(
  () => import(/* webpackChunkName: "Filters" */ "./Filters")
);
import Sort from "./Sort";
const QueryEditor = React.lazy(
  () => import(/* webpackChunkName: "QueryEditor" */ "./QueryEditor")
);
const Visualization = React.lazy(
  () => import(/* webpackChunkName: "Visualization" */ "../Visualization")
);

const timeFromNow = (timestamp: object) => moment(timestamp).fromNow();

interface IDatasourceSelectObject {
  key: string;
  metadata?: MetadataObject;
  text: string;
  type?: string;
  value: string;
}

export interface IQueryTabProps extends IBaseProps {
  analysisId: string;
  queryId: string;
  versionId?: string;
  usersStatus: UsersStatus;
  onRemove: (queryId: string) => void;
}

export interface IQueryTabState extends IBaseState {
  query: Query;
  version: Version;
  abstractQuery: AbstractQuery;
  queryInfo: any;
  data: VersionSuccessResult;
  datasources: IDatasourceSelectObject[];
  datasourceId: string;
  datasourceMetadata: any;
  sql: string;
  fetchingData: boolean;
  abstractQueryChanged: boolean;
  sqlMode: boolean;
  isError: boolean;
  errors: VersionErrorResult;
  openVersionHistory: boolean;
  currentSidebarTab: number;
  showSchema: boolean;
  openDeleteModal: boolean;
  openEditNameModal: boolean;
  activeUsers: any[];
  selectedVisualization: VisualizationType;
  schemaSelectedColumn: any;
}

export default class QueryTab extends Base<IQueryTabProps, IQueryTabState> {
  private static getInitialData = () => {
    return {
      data: [],
      header: [],
      datasourceId: "",
      status: -1,
    };
  };
  private static getInitialAbstractQuery = () => {
    return {
      columns: [],
      doubts: [],
      complete: true,
      filters: {
        jsonLogic: "{}",
      },
      hidden: [],
      joins: [],
      limit: {
        limit: 1000,
        offset: 0,
      },
      messages: [],
      notJoined: false,
      selectDistinct: false,
      orderBys: [],
    };
  };
  private static getInitialState = () => {
    return {
      query: undefined,
      version: null,
      abstractQuery: { ...QueryTab.getInitialAbstractQuery() },
      queryInfo: {
        name: "",
      },
      data: { ...QueryTab.getInitialData() },
      datasourceId: "",
      datasourceMetadata: null,
      datasources: [],
      fetchingData: false,
      abstractQueryChanged: false,
      listeners: [],
      sql: "",
      sqlMode: false,
      isError: false,
      errors: {
        status: -1,
        failureReason: {},
      },
      openVersionHistory: false,
      currentSidebarTab: 0,
      showSchema: false,
      openDeleteModal: false,
      openEditNameModal: false,
      activeUsers: [],
      selectedVisualization: null,
      schemaSelectedColumn: null,
    };
  };
  constructor(props: IQueryTabProps) {
    super(props);

    this.state = {
      ...Base.BASE_INITAL_STATE,
      ...QueryTab.getInitialState(),
    };
  }

  public render() {
    const { datasourceId } = this.state;
    if (datasourceId) {
      return this.renderQuery();
    } else {
      return this.renderEmptyDatasource();
    }
  }

  private renderEmptyDatasource = () => {
    const { datasources, datasourceId } = this.state;
    return (
      <div>
        <Container className="QueryTab__container">
          {datasources && datasources.length > 0 && (
            <Segment style={{ height: "calc(100vh - 50px)" }}>
              <div
                style={{
                  width: "300px",
                  margin: "0 auto",
                  position: "relative",
                  top: "calc(50% - 30px)",
                }}
              >
                <Icon
                  name="database"
                  size="large"
                  style={{ lineHeight: "15px" }}
                />
                <Dropdown
                  className="empty-datasource-dropdown"
                  inline={true}
                  search={true}
                  options={datasources}
                  placeholder="Select a datasource"
                  value={datasourceId}
                  onChange={this.handleChangeDatasource}
                />
              </div>
            </Segment>
          )}
        </Container>
      </div>
    );
  };

  private renderQuery() {
    const {
      datasources,
      datasourceId,
      datasourceMetadata,
      abstractQuery,
      data,
      fetchingData,
      sqlMode,
      sql,
      isError,
      errors,
      query,
      version,
      showSchema,
      openDeleteModal,
      queryInfo,
      openEditNameModal,
      activeUsers,
      selectedVisualization,
      schemaSelectedColumn,
    } = this.state;
    const { orgId, authUser, analysisId } = this.props;

    return (
      <div>
        <Container className="QueryTab__container">
          {datasources && datasources.length > 0 && (
            <React.Fragment>
              <Segment className="QueryTab__header">
                <span>Datasource </span>
                {datasourceId && datasources && (
                  <Label
                    style={{
                      background: "0 0",
                      fontSize: "14px",
                      padding: "0px 5px 0px 8px",
                    }}
                  >
                    {datasources.find((d) => d.value === datasourceId).text}
                  </Label>
                )}
                {data.executedAt && (
                  <span style={{ marginLeft: "15px" }}>
                    Last ran {timeFromNow(data.executedAt)}
                  </span>
                )}
                <div
                  style={{
                    display: "inline-block",
                    height: "25px",
                    cursor: "pointer",
                    float: "right",
                    width: "106px",
                    textAlign: "center",
                  }}
                >
                  {this.state.sqlMode && (
                    <Popup
                      content="Switch to search editor"
                      position="bottom left"
                      trigger={
                        <Label
                          className="switch-mode"
                          onClick={() => {
                            this.setState({ sqlMode: false });
                          }}
                        >
                          Search editor
                        </Label>
                      }
                    />
                  )}
                  {!this.state.sqlMode && (
                    <Popup
                      content="Switch to sql editor"
                      position="bottom left"
                      trigger={
                        <Label
                          className="switch-mode"
                          onClick={() => {
                            this.setState({ sqlMode: true });
                          }}
                        >
                          Sql editor
                        </Label>
                      }
                    />
                  )}
                </div>
                <div
                  style={{
                    display: "inline-block",
                    height: "25px",
                    cursor: "pointer",
                    float: "right",
                  }}
                >
                  <Popup
                    content="Edit query name"
                    position="bottom left"
                    trigger={
                      <Icon
                        link={true}
                        name="edit"
                        onClick={this.handleOpenEditNameModel}
                      />
                    }
                  />

                  {queryInfo && (
                    <EditQueryNameModal
                      orgId={orgId}
                      authUser={authUser}
                      modal={openEditNameModal}
                      defaultName={queryInfo.name}
                      closeModal={this.handleCloseEditName}
                      save={this.handleSaveName}
                    />
                  )}
                  <Popup
                    content="Delete query"
                    position="bottom left"
                    trigger={
                      <Icon
                        link={true}
                        name="trash alternate outline"
                        onClick={this.deleteModal}
                      />
                    }
                  />
                  {queryInfo && (
                    <Confirm
                      open={openDeleteModal}
                      content={`Are you sure you want to delete "${queryInfo.name}"`}
                      cancelButton="Cancel"
                      confirmButton="Delete"
                      onCancel={this.handleCancelDelete}
                      onConfirm={this.handleConfirmDelete}
                    />
                  )}
                </div>
                {activeUsers && (
                  <div
                    style={{
                      display: "inline-block",
                      height: "25px",
                      cursor: "pointer",
                      float: "right",
                      width: "106px",
                      textAlign: "center",
                    }}
                  >
                    {_.filter(activeUsers, (i) => {
                      const userStatus: UserStatus = this.props.usersStatus[
                        i.id
                      ];
                      return userStatus && userStatus.online;
                    })
                      .slice(0, 3)
                      .map((member, index) => {
                        return (
                          <React.Fragment key={`${member.id}_${index}`}>
                            {Base.getAvatar(
                              member,
                              this.props.usersStatus[member.id]
                            )}
                            {activeUsers.length > 3 && (
                              <Label circular={true}>...</Label>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </div>
                )}
              </Segment>
              <Segment className="query-area">
                {!sqlMode && (
                  <SearchBox
                    authUser={this.props.authUser}
                    version={this.state.version}
                    doubts={abstractQuery.doubts}
                    metadata={datasourceMetadata}
                    value=""
                    onChange={this.handleMessageChange}
                    onClearQuery={() => true}
                  />
                )}
                {!sqlMode && (
                  <Segment className="dataTable" attached="top">
                    <Dimmer active={fetchingData} inverted={true}>
                      <Loader size="small">Fetching data</Loader>
                    </Dimmer>
                    <Grid columns="2">
                      <Grid.Row>
                        <Grid.Column
                          width={4}
                          style={{ backgroundColor: "#ffffff" }}
                        >
                          <DataTable
                            orgId={orgId}
                            datasourceId={datasourceId}
                            response={data}
                            abstractQuery={abstractQuery}
                            handleAddColumn={this.handleAddColumn}
                            handleAggregationChanged={
                              this.handleAggregationChanged
                            }
                            handleRemoveColumn={this.handleRemoveColumn}
                            showSchema={this.handleShowSchema}
                          />
                        </Grid.Column>
                        <Grid.Column width={12} className="filters-layout">
                          <div style={{ height: "200px" }}>
                            {!_.isEmpty(abstractQuery.columns) &&
                              !_.isNull(datasourceMetadata) && (
                                <React.Suspense
                                  fallback={
                                    <Dimmer inverted={true}>
                                      <Loader size="small">
                                        Fetching data
                                      </Loader>
                                    </Dimmer>
                                  }
                                >
                                  <Filters
                                    metadata={datasourceMetadata}
                                    resultColumns={abstractQuery.columns}
                                    existingFilters={
                                      abstractQuery.filters.jsonLogic
                                    }
                                    onFilterChange={this.handleFilterChange}
                                  />
                                </React.Suspense>
                              )}
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                )}
                {sqlMode && (
                  <Segment
                    className={`dataTable ${
                      this.state.sqlMode ? "sql-mode" : ""
                    }`}
                    attached="top"
                  >
                    <Dimmer active={fetchingData} inverted={true}>
                      <Loader size="small">Fetching data</Loader>
                    </Dimmer>
                    {!_.isNull(datasourceMetadata) && (
                      <React.Suspense
                        fallback={
                          <Dimmer inverted={true}>
                            <Loader size="small">Fetching data</Loader>
                          </Dimmer>
                        }
                      >
                        <QueryEditor
                          authUser={this.props.authUser}
                          version={this.state.version}
                          value={sql}
                          newColumnFromSchema={schemaSelectedColumn}
                          metadata={datasourceMetadata}
                          onChange={this.handleCodeChange}
                        />
                      </React.Suspense>
                    )}
                  </Segment>
                )}

                <Segment className="datatable-toolbar" attached="bottom">
                  {!sqlMode && this.renderTableToolbar()}
                  {sqlMode && this.renderEditorToolbar()}
                </Segment>
              </Segment>
              {showSchema && datasourceMetadata && (
                <Segment placeholder={true} className="analysis-layout-chart">
                  <BrowseColumns
                    metadata={datasourceMetadata}
                    orgId={orgId}
                    authUser={this.props.authUser}
                    resultColumns={abstractQuery.columns}
                    addColumn={this.handleAddColumn}
                    hideSchema={this.hideSchema}
                  />
                </Segment>
              )}

              {data.status === -1 && !showSchema && (
                <Segment placeholder={true} className="analysis-layout-chart">
                  <Dimmer active={fetchingData} inverted={true}>
                    <Loader size="small">Fetching data</Loader>
                  </Dimmer>
                  <Header icon={true}>
                    <Icon name="pie chart" />
                    <Icon name="chart bar" />
                    <Icon name="chart area" />
                  </Header>
                  <Segment.Inline>
                    Ask in English / Use Visual Editor / Write SQL.
                  </Segment.Inline>
                </Segment>
              )}
              {data.status !== -1 && !showSchema && isError && (
                <Segment placeholder={true} className="analysis-layout-chart">
                  <Dimmer active={fetchingData} inverted={true}>
                    <Loader size="small">Fetching data</Loader>
                  </Dimmer>
                  <Header icon={true}>
                    <Icon name="pie chart" />
                    <Icon name="chart bar" />
                    <Icon name="chart area" />
                  </Header>
                  <Segment.Inline>
                    <Message
                      error={true}
                      header="Error"
                      list={_.map(
                        errors.failureReason.errors,
                        (err) => err.message
                      )}
                    />
                  </Segment.Inline>
                </Segment>
              )}
              {/* {data.status !== -1 && (
                <Segment className="analysis-layout-chart">
                  <Dimmer active={fetchingData} inverted={true}>
                    <Loader size="small">Fetching data</Loader>
                  </Dimmer>
                  <ResultDataTable response={data} />
                </Segment>
              )} */}
              {data.status !== -1 && !showSchema && !isError && (
                <Segment className="analysis-layout-chart">
                  <Dimmer active={fetchingData} inverted={true}>
                    <Loader size="small">Fetching data</Loader>
                  </Dimmer>
                  <React.Suspense
                    fallback={
                      <Dimmer inverted={true}>
                        <Loader size="small">Fetching data</Loader>
                      </Dimmer>
                    }
                  >
                    <Visualization
                      authUser={this.props.authUser}
                      orgId={this.props.orgId}
                      datasourceId={datasourceId}
                      sql={sql}
                      data={data}
                      resultColumns={abstractQuery.columns}
                      analysisId={analysisId}
                      queryId={query.id}
                      versionId={version.id}
                      seletedVisualization={selectedVisualization}
                    />
                  </React.Suspense>
                </Segment>
              )}
            </React.Fragment>
          )}
        </Container>
      </div>
    );
  }

  public componentDidMount = () => {
    this.getData();
    this.getDatasources();
    this.listenToActiveUsers();
  };

  private listenToActiveUsers = () => {
    const { authUser, orgId, analysisId, queryId } = this.props;
    const query = new Query(orgId, analysisId, queryId);
    this.getListner(
      query.activeUsers(),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const dataObj = snapshot.val();
        if (dataObj) {
          const allActiveUsers = Object.keys(dataObj).map((id: string) => {
            return {
              id,
              ...dataObj[id],
            };
          });
          const activeUsers = _.filter(allActiveUsers, (i: any) => {
            return i.id !== authUser.uid;
          });
          this.setState({ activeUsers });
        }
      }
    );
  };

  public getDatasources = () => {
    const { orgId, authUser } = this.props;
    const datasources = new Datasources(orgId);
    this.getList(datasources, authUser.uid, (data: any[]) => {
      const datasourcesList = data.map((item) => {
        return {
          key: item.id,
          text: item.name,
          type: item.type,
          value: item.id,
        };
      });
      this.setState({
        datasources: datasourcesList,
      });
    });
  };

  private getDatasourceMetadata = (datasourceId: string) => {
    const { orgId } = this.props;
    const datasources = new Datasources(orgId);
    this.getOnceListner(
      datasources.getMetadata(datasourceId),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const metadata = snapshot.val();
        if (metadata) {
          this.setState({ datasourceMetadata: metadata });
        }
      }
    );
  };

  private handleChangeDatasource = (event: any, { value }: DropdownProps) => {
    const datasourceId: string = value + "";
    this.handleUpdateDatasourceId(datasourceId, () => {
      this.getDatasourceMetadata(datasourceId);
      this.setState({
        abstractQuery: QueryTab.getInitialAbstractQuery(),
        data: QueryTab.getInitialData(),
        datasourceId,
        sql: "",
        showSchema: true,
      });
    });
  };

  private getData = () => {
    const { orgId, analysisId, queryId, versionId } = this.props;
    const query = new Query(orgId, analysisId, queryId);
    this.getListner(
      query.getConfig(queryId),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const qConfig = snapshot.val();
        if (qConfig) {
          this.setState({
            query,
            queryInfo: snapshot.val(),
            datasourceId: qConfig.datasourceId,
          });
          this.getDatasourceMetadata(qConfig.datasourceId);
          // we are listening to the latest version to support collabration
          // but we need to make sure new version request is always from the user actions
          this.getListner(
            query.getVersions().orderByKey().limitToLast(1),
            Event.Value,
            (versionSnapshot: firebase.database.DataSnapshot) => {
              const latestVersion: any = versionSnapshot.val();
              if (latestVersion) {
                let latestVersionKey = Object.keys(latestVersion)[0];
                if (versionId) {
                  latestVersionKey = versionId;
                }

                const newVersion = new Version(
                  orgId,
                  analysisId,
                  queryId,
                  latestVersionKey
                );
                this.getVersion(newVersion);
              }
            }
          );
        }
      }
    );
  };

  private renderEditorToolbar = () => {
    return (
      <React.Fragment>
        <div className="datatable-toolbar-pagination">
          <div
            style={{
              display: "inline-block",
              lineHeight: "20px",
              padding: "10px",
            }}
          >
            <Popup
              position="bottom left"
              content="Format SQL"
              trigger={
                <Icon
                  name="file code outline"
                  link={true}
                  onClick={this.handleFormatQueryClick}
                />
              }
            />
            {"           "}
            <Popup
              position="bottom left"
              content="Version history"
              trigger={
                <Icon
                  link={true}
                  name="code branch"
                  onClick={(e: React.SyntheticEvent) =>
                    this.setState({ openVersionHistory: true })
                  }
                />
              }
            />
            <a onClick={this.handleShowSchema}>Show schema </a>
            <VersionHistoryModal
              authUser={this.props.authUser}
              open={this.state.openVersionHistory}
              setOpen={this.handleVersionSetOpen}
              query={this.state.query}
            />
          </div>

          <Popup
            position="bottom left"
            content="Run query"
            trigger={
              <Button
                primary={true}
                positive={true}
                style={{ float: "right" }}
                loading={this.state.fetchingData}
                onClick={this.handleRunQuery}
                icon="play"
              />
            }
          />
        </div>
      </React.Fragment>
    );
  };

  private renderTableToolbar = () => {
    const { fetchingData, abstractQuery } = this.state;
    return (
      <React.Fragment>
        {!fetchingData && !_.isEmpty(abstractQuery.columns) && (
          <div className="datatable-toolbar-pagination">
            <div className="datatable-toolbar-pagination-limit">
              <Label
                style={{
                  background: "0 0",
                  fontSize: "14px",
                  padding: "0px 5px 0px 8px",
                }}
              >
                Limit
              </Label>
              <EditableLabel
                text={abstractQuery.limit?.limit + ""}
                labelClassName="semantic-input-limit-label"
                inputClassName="semantic-input-limit-input"
                inputWidth="150px"
                inputHeight="10px"
                inputMaxLength={50}
                onFocusOut={this.handleLimitChange}
              />
              <Label
                style={{
                  background: "0 0",
                  fontSize: "14px",
                  padding: "0px 5px 0px 8px",
                }}
              >
                Sort by
              </Label>
            </div>
            <div
              style={{
                display: "inline-block",
                lineHeight: "20px",
                padding: "10px 0",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              {!fetchingData && !_.isEmpty(abstractQuery.columns) && (
                <Sort
                  resultColumns={abstractQuery.columns}
                  orderBys={abstractQuery.orderBys}
                  onSortChange={this.handleSortChange}
                />
              )}
            </div>

            {this.state.abstractQueryChanged && (
              <Popup
                position="bottom left"
                content="Run query"
                trigger={
                  <Button
                    style={{ float: "right", marginLeft: "10px" }}
                    icon="play"
                    // disabled={!this.state.abstractQueryChanged}
                    positive={true}
                    onClick={this.handleAqSubmit}
                  />
                }
              />
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  private handleShowSchema = () => {
    this.setState({ showSchema: !this.state.showSchema });
  };

  private hideSchema = () => {
    this.setState({ showSchema: false });
  };

  private handleClearQuery = () => {
    this.setState({
      abstractQuery: QueryTab.getInitialAbstractQuery(),
      data: QueryTab.getInitialData(),
      sql: "",
      fetchingData: false,
    });
  };

  private handleFormatQueryClick = () => {
    let { sql } = this.state;
    sql = sqlFormatter.format(sql);
    this.setState({ sql });
  };

  private handleVersionSetOpen = () => {
    this.setState({ openVersionHistory: false });
  };

  private handleCodeChange = (sql: string) => {
    this.setState({ sql });
  };

  private handleMessageChange = (value: string) => {
    this.createNLQuery(value, this.state.abstractQuery);
  };

  private handleAqSubmit = () => {
    const req: VersionObject = {
      audit: {
        createdBy: this.props.authUser.uid,
      },
      request: {
        action: {
          type: "table",
          value: "AQ changed",
        },
        aq: this.state.abstractQuery,
        datasourceId: this.state.datasourceId,
      },
    };
    this.createVersion(req);
  };

  private handleRunQuery = () => {
    const { orgId, analysisId, queryId } = this.props;
    const { sql } = this.state;
    const query = new Query(orgId, analysisId, queryId);
    this.setState({ fetchingData: true });
    const queryRequestAction: VersionRequestAction = {
      type: "sql",
      value: sql,
    };
    const queryRequest: VersionRequest = {
      action: queryRequestAction,
      datasourceId: this.state.datasourceId,
    };
    const queryObject = {
      audit: {
        createdBy: this.props.authUser.uid,
      },
      request: queryRequest,
    };
    query.createVersion(queryObject);
  };

  private handleSortChange = (orderBys: OrderBy[]) => {
    const { abstractQuery } = this.state;
    const newAbstractQuery: AbstractQuery = cloneWithOutReference(
      abstractQuery
    );
    newAbstractQuery.orderBys = orderBys;
    // const message = `Sort Changed`;
    // this.createTransitionQuery(newAbstractQuery, message);
    this.setState({
      abstractQuery: newAbstractQuery,
      abstractQueryChanged: true,
    });
  };

  private handleFilterChange = (tree: any) => {
    const { abstractQuery } = this.state;
    const newAbstractQuery: AbstractQuery = cloneWithOutReference(
      abstractQuery
    );
    newAbstractQuery.filters = {
      jsonLogic: tree,
    };
    const newAq = AbstractQueryFactory.applyJoinsOnAq(
      newAbstractQuery,
      this.state.datasourceMetadata.joins
    );
    // const message = `Filter applied`;
    // this.createTransitionQuery(newAq, message);
    this.setState({ abstractQuery: newAq, abstractQueryChanged: true });
  };

  private handleLimitChange = (value) => {
    const { abstractQuery } = this.state;
    const newAbstractQuery = cloneWithOutReference(abstractQuery);
    newAbstractQuery.limit = {
      limit: +value,
      offset: 0,
    };
    // const message = `Limit changed to ${newAbstractQuery.limit.limit}`;
    this.setState({
      abstractQuery: newAbstractQuery,
      abstractQueryChanged: true,
    });
    // this.createTransitionQuery(newAbstractQuery, message);
  };

  private handleRemoveColumn = (columnIndex: number) => {
    const { abstractQuery } = this.state;
    if (abstractQuery.columns.length <= 1) {
      this.handleClearQuery();
      return true;
    }
    const newAbstractQuery = cloneWithOutReference(abstractQuery);
    newAbstractQuery.columns.splice(columnIndex, 1);
    const newAq = AbstractQueryFactory.applyJoinsOnAq(
      newAbstractQuery,
      this.state.datasourceMetadata.joins
    );
    // const message = `Removed column ${this.displayColumnName(removedCol[0])}`;
    // this.createTransitionQuery(newAq, message);
    this.setState({ abstractQuery: newAq, abstractQueryChanged: true });
  };

  private handleAggregationChanged = (
    columnIndex: number,
    aggregation: FunctionName
  ) => {
    const { abstractQuery } = this.state;
    const newAbstractQuery = cloneWithOutReference(abstractQuery);
    const thisColumn: ResultColumn = newAbstractQuery.columns[columnIndex];
    // const message = "";
    if (aggregation === FunctionName.NONE) {
      if (thisColumn.type === ResultColumnType.FUNC) {
        const funcColumn: Func = thisColumn as Func;
        newAbstractQuery.columns[columnIndex] = funcColumn.column;
      } else {
        const col: Column = thisColumn as Column;
        newAbstractQuery.columns[columnIndex] = col;
      }
      // message = `Removed function on ${this.displayColumnName(thisColumn)}`;
    } else {
      if (thisColumn.type === ResultColumnType.FUNC) {
        newAbstractQuery.columns[columnIndex].name = aggregation;
      } else {
        const col: Column = thisColumn as Column;
        const funcColumn: Func = {
          type: ResultColumnType.FUNC,
          column: col,
          name: aggregation,
          alias: "",
          defaultFunc: FunctionName.NONE,
          dataType: col.dataType,
        };
        newAbstractQuery.columns[columnIndex] = funcColumn;
      }
    }

    this.setState({
      abstractQuery: newAbstractQuery,
      abstractQueryChanged: true,
    });
  };

  private handleAddColumn = (
    tableName: string,
    column: ColumnObject,
    metadata: any
  ) => {
    if (this.state.sqlMode) {
      this.setState({
        schemaSelectedColumn: {
          table: tableName,
          column,
        },
      });
    } else {
      const resultColumn: ResultColumn = AbstractQueryFactory.getResultColumn(
        tableName,
        column
      );

      const { abstractQuery } = this.state;
      const newAq = AbstractQueryFactory.addResultColumnToAq(
        abstractQuery,
        resultColumn,
        metadata.joins
      );
      // const message = `Added new column ${this.displayColumnName(resultColumn)}`;
      // this.createTransitionQuery(newAq, message);
      this.setState({
        abstractQuery: newAq,
        abstractQueryChanged: true,
      });
    }
  };

  private fixJoinsonAq = (abstractQuery: AbstractQuery, metadata: any) => {
    const newAq = AbstractQueryFactory.applyJoinsOnAq(
      abstractQuery,
      metadata.joins
    );
    this.createTransitionQuery(newAq, "417");
  };

  private createTransitionQuery = (
    abstractQuery: AbstractQuery,
    message: string
  ) => {
    const req: VersionObject = {
      audit: {
        createdBy: this.props.authUser.uid,
      },
      request: {
        action: {
          type: "table",
          value: message,
        },
        aq: abstractQuery,
        datasourceId: this.state.datasourceId,
      },
    };
    this.createVersion(req);
  };

  private createNLQuery = (nlq: string, abstractQuery: AbstractQuery) => {
    const req: VersionObject = {
      audit: {
        createdBy: this.props.authUser.uid,
      },
      request: {
        action: {
          type: "nlq",
          value: nlq,
        },
        aq: abstractQuery,
        datasourceId: this.state.datasourceId,
      },
    };
    this.createVersion(req);
  };

  private addToHistory = (req: VersionObject, versionId: string) => {
    const message: MessageObject = {
      content: req.request.action.value,
      type: MessageType.U2B,
      from: this.props.authUser.email,
      fromName: this.props.authUser.displayName,
      to: [],
      audit: req.audit,
      queryId: this.props.queryId,
      queryVersion: versionId,
    };
    this.state.query.createHistory(message);
  };

  private createVersion = (versionObject: VersionObject) => {
    const { orgId, analysisId, queryId } = this.props;
    this.setState({ fetchingData: true, showSchema: false });

    const query = new Query(orgId, analysisId, queryId);
    const newVersion: any = query.createVersion(versionObject);

    if (versionObject.request.action.value !== "417") {
      this.addToHistory(versionObject, newVersion.key);
    }
  };

  private getVersion = (version: Version) => {
    this.setState({ version });
    this.getOnceListner(
      version.getRequest(),
      Event.Value,
      (requestSnapshot: firebase.database.DataSnapshot) => {
        const request: VersionRequest = requestSnapshot.val();
        this.setState({ datasourceId: request.datasourceId });
        if (request.action.type === "sql") {
          this.getSqlResponse(version, request);
        } else {
          this.getNonSqlResponse(version, request);
        }
      }
    );
  };

  private getSqlResponse = (version: Version, request: VersionRequest) => {
    this.getOnceListner(
      version.getResult(),
      Event.Value,
      (resultSnapshot: firebase.database.DataSnapshot) => {
        const newAq: AbstractQuery = QueryTab.getInitialAbstractQuery();
        const response: VersionResult = resultSnapshot.val();
        if (!response) {
          return;
        }
        if (response.status === 200) {
          const data: VersionSuccessResult = response as VersionSuccessResult;
          newAq.columns = data.header;
          this.setState({
            abstractQuery: newAq,
            abstractQueryChanged: false,
            data,
            isError: false,
            fetchingData: false,
            sql: sqlFormatter.format(request.action.value),
            sqlMode: true,
            showSchema: false,
            selectedVisualization: data.selectedVisualization,
          });
        } else if (response.status === 204) {
          const data: VersionSuccessResult = response as VersionSuccessResult;
          this.setState({
            abstractQuery: newAq,
            abstractQueryChanged: false,
            data,
            isError: false,
            fetchingData: false,
            showSchema: false,
          });
        } else {
          let errors: VersionErrorResult = response as VersionErrorResult;
          errors = errors || {
            status: response.status,
            failureReason: {
              errors: [
                {
                  message: "Query Failed",
                },
              ],
            },
          };
          this.setState({
            abstractQuery: newAq,
            isError: true,
            errors,
            data: {
              status: response.status,
              data: [],
            },
            fetchingData: false,
            showSchema: false,
          });
        }
      }
    );
  };

  private getNonSqlResponse = (version: Version, request: VersionRequest) => {
    this.getOnceListner(
      version.getTranslation(),
      Event.Value,
      (translationSnapshot: firebase.database.DataSnapshot) => {
        if (translationSnapshot.val()) {
          let newAq: AbstractQuery = request.aq;
          const translation: VersionTranslation = translationSnapshot.val();
          if (translation.status === 200 || translation.status === 417) {
            const successTranslation: VersionSuccessTranslation = translation as VersionSuccessTranslation;
            if (successTranslation.aq) {
              newAq = successTranslation.aq;
              newAq.columns = newAq.columns || [];
              newAq.joins = newAq.joins || [];
              if (translation.status === 417) {
                this.fixJoinsonAq(newAq, this.state.datasourceMetadata);
              }
            }
            if (translation.status === 200) {
              this.getResult(version, successTranslation, newAq);
            }
          } else if (translation.status === 204) {
            this.handleClearQuery();
          } else {
            const translationError: VersionErrorTranslation = translation as VersionErrorTranslation;
            const errors = translationError || {
              status: translation.status,
              failureReason: {
                errors: [
                  {
                    message: "Translation Failed",
                  },
                ],
              },
            };
            this.setState({
              abstractQuery: newAq,
              isError: true,
              errors,
              data: {
                status: translation.status,
                data: [],
              },
              fetchingData: false,
            });
          }
        }
      }
    );
  };

  private getResult = (
    version: Version,
    translation: VersionSuccessTranslation,
    newAq: AbstractQuery
  ) => {
    this.setState({ sql: sqlFormatter.format(translation.sql) });
    this.getOnceListner(
      version.getResult(),
      Event.Value,
      (resultSnapshot: firebase.database.DataSnapshot) => {
        const response: VersionResult = resultSnapshot.val();
        if (!response) {
          return;
        }
        if (response.status === 200) {
          const data: VersionSuccessResult = response as VersionSuccessResult;
          this.setState({
            abstractQuery: newAq,
            abstractQueryChanged: false,
            data,
            isError: false,
            fetchingData: false,
            showSchema: false,
            selectedVisualization: data.selectedVisualization,
          });
        } else if (response.status === 204) {
          const data: VersionSuccessResult = response as VersionSuccessResult;
          this.setState({
            abstractQuery: newAq,
            abstractQueryChanged: false,
            data,
            isError: false,
            fetchingData: false,
            showSchema: false,
          });
        } else {
          let errors: VersionErrorResult = response as VersionErrorResult;
          errors = errors || {
            status: translation.status,
            failureReason: {
              errors: [
                {
                  message: "Query Failed",
                },
              ],
            },
          };
          this.setState({
            abstractQuery: newAq,
            isError: true,
            errors,
            data: {
              status: response.status,
              data: [],
            },
            fetchingData: false,
            showSchema: false,
          });
        }
      }
    );
  };

  private deleteModal = () => {
    this.setState({ openDeleteModal: true });
  };

  private handleCancelDelete = () => {
    this.setState({ openDeleteModal: false });
  };

  private handleConfirmDelete = () => {
    const { authUser, orgId, onRemove, queryId } = this.props;
    const { query } = this.state;
    query
      .requestDeleteQuery(authUser.uid)
      .then((requestRef: firebase.database.Reference) => {
        this.getListner(
          query.getApiResponse(orgId, authUser.uid, requestRef.key),
          Event.Value,
          (snapshot: firebase.database.DataSnapshot) => {
            const resp = snapshot.val();
            if (resp) {
              onRemove(queryId);
              toast({
                type: "success",
                icon: "info circle",
                title: `Successfully deleted query`,
                animation: "slide up",
                time: 5000,
              });
            }
          }
        );
      });
  };

  private handleOpenEditNameModel = () => {
    this.setState({ openEditNameModal: true });
  };
  private handleCloseEditName = () => {
    this.setState({ openEditNameModal: false });
  };
  private handleSaveName = (name: string, callback: () => void) => {
    const { query } = this.state;
    query.updateConfig({ name }).then(() => {
      toast({
        type: "success",
        icon: "info circle",
        title: `Successfully changed query name.`,
        animation: "slide up",
        time: 5000,
      });
      callback();
      this.handleCloseEditName();
    });
  };
  private handleUpdateDatasourceId = (
    datasourceId: string,
    callback: () => void
  ) => {
    const { query } = this.state;
    query.updateConfig({ datasourceId }).then(() => {
      callback();
    });
  };
}



// WEBPACK FOOTER //
// ./src/components/Query/QueryTab.tsx